import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Copy } from 'lucide-react';
import { mensajeDeError } from '../lib/mensajeDeError';
import { soloCifras } from '../lib/segundoFactor';

// EL SEGUNDO PASO DE ENTRAR DE LOS ADMINS (Francisco, 28-10: «solo admins, 30 días y
// obligatorio»). Sale en la pantalla de entrar después de la contraseña:
//   - «configurar»: la primera vez. QR para Google Authenticator, la clave por si no se puede
//     escanear y el primer código. Al activarlo, los códigos de repuesto, una sola vez.
//   - «codigo»: las demás. El código de la app, o uno de repuesto si no tiene el móvil.
// «Recordar este dispositivo» hace que ese navegador no lo pida en 30 días.
// La regla y el porqué, en `backend/core/segundo_factor.py`.

const CAMPO = 'h-14 bg-bg-input-dark border-0 text-white placeholder:text-white/40 rounded-xl focus:ring-2 focus:ring-brand';
const BOTON = 'w-full h-14 bg-brand hover:bg-brand-dark text-white font-bold text-lg rounded-full transition-all duration-200 shadow-lg shadow-brand/30';

const SegundoFactorPaso = ({ email, ticket, paso, onEntrar, onVolver }) => {
    const { api, segundoFactor } = useAuth();
    const configurando = paso === 'configurar';
    const [qr, setQr] = useState(null);
    const [codigo, setCodigo] = useState('');
    const [repuesto, setRepuesto] = useState('');
    const [conRepuesto, setConRepuesto] = useState(false);
    const [recordar, setRecordar] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [codigosDeRepuesto, setCodigosDeRepuesto] = useState(null);
    const [entrar, setEntrar] = useState(null);

    useEffect(() => {
        if (!configurando) return;
        let vivo = true;
        api.post('/auth/segundo-factor/configurar', { ticket })
            .then(r => { if (vivo) setQr(r.data); })
            .catch(e => { if (vivo) { toast.error(mensajeDeError(e, 'No se pudo preparar la verificación')); onVolver(); } });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- una vez por ticket
    }, [ticket, configurando]);

    const enviar = async (e) => {
        e.preventDefault();
        setEnviando(true);
        try {
            const datos = conRepuesto ? { repuesto, recordar } : { codigo, recordar };
            const r = await segundoFactor(email, ticket, datos, configurando);
            if (r.codigosDeRepuesto?.length) {
                setEntrar(() => r.entrar);
                setCodigosDeRepuesto(r.codigosDeRepuesto);
            } else {
                onEntrar(await r.entrar());
            }
        } catch (err) {
            if (err.response?.status === 401) onVolver();
            toast.error(mensajeDeError(err, 'Ese código no vale. Escribe el que te sale ahora en la app.'));
            setCodigo('');
        } finally {
            setEnviando(false);
        }
    };

    const copiar = (texto, aviso) => {
        navigator.clipboard.writeText(texto)
            .then(() => toast.success(aviso))
            .catch(() => toast.error('No se pudo copiar'));
    };

    if (codigosDeRepuesto) {
        return (
            <div className="w-full space-y-4" data-testid="segundo-factor-repuesto">
                <p className="text-white text-center font-semibold">Guarda estos códigos de repuesto</p>
                <p className="text-white/60 text-sm text-center">
                    Si pierdes el móvil, cada uno te deja entrar una vez. No los volverás a ver.
                </p>
                <div className="grid grid-cols-2 gap-2 bg-bg-input-dark rounded-xl p-4 font-mono text-white text-center">
                    {codigosDeRepuesto.map(c => <span key={c}>{c}</span>)}
                </div>
                <button type="button" onClick={() => copiar(codigosDeRepuesto.join('\n'), 'Códigos copiados')}
                    className="w-full flex items-center justify-center gap-2 text-brand text-sm hover:underline">
                    <Copy className="w-4 h-4" /> Copiar los códigos
                </button>
                <Button type="button" className={BOTON} onClick={async () => onEntrar(await entrar())} data-testid="segundo-factor-guardados">
                    Ya los he guardado
                </Button>
            </div>
        );
    }

    return (
        <form onSubmit={enviar} className="w-full space-y-4" data-testid="segundo-factor">
            <div className="flex flex-col items-center gap-2 text-center">
                <ShieldCheck className="w-8 h-8 text-brand" />
                <p className="text-white font-semibold">Verificación en dos pasos</p>
                <p className="text-white/60 text-sm">
                    {configurando
                        ? 'Las cuentas de administrador la necesitan. Escanea el código con Google Authenticator y escribe el número que te salga.'
                        : conRepuesto
                            ? 'Escribe uno de tus códigos de repuesto.'
                            : 'Escribe el código que te sale en Google Authenticator.'}
                </p>
            </div>

            {configurando && (
                qr ? (
                    <div className="flex flex-col items-center gap-3">
                        <img src={`data:image/svg+xml;utf8,${encodeURIComponent(qr.qr_svg)}`} alt="Código QR para Google Authenticator"
                            className="w-48 h-48 bg-white rounded-xl p-2" data-testid="segundo-factor-qr" />
                        <p className="text-white/50 text-xs text-center">Si no puedes escanearlo, escribe esta clave en la app:</p>
                        <button type="button" onClick={() => copiar(qr.clave.replace(/\s/g, ''), 'Clave copiada')}
                            className="font-mono text-white text-sm bg-bg-input-dark rounded-lg px-3 py-2 text-center"
                            data-testid="segundo-factor-clave">
                            {/* En dos filas de cuatro grupos: dejada al ancho, en móvil partía un grupo. */}
                            {qr.clave.split(' ').slice(0, 4).join(' ')}<br />{qr.clave.split(' ').slice(4).join(' ')}
                        </button>
                    </div>
                ) : (
                    <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-white/60" /></div>
                )
            )}

            {conRepuesto ? (
                <Input type="text" placeholder="Código de repuesto" autoComplete="off" autoFocus
                    className={`${CAMPO} text-center font-mono tracking-widest`}
                    value={repuesto} onChange={e => setRepuesto(e.target.value)} required
                    data-testid="segundo-factor-repuesto-campo" />
            ) : (
                <Input type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" autoFocus
                    className={`${CAMPO} text-center text-2xl font-mono tracking-[0.4em]`}
                    value={codigo} onChange={e => setCodigo(soloCifras(e.target.value))} required minLength={6}
                    data-testid="segundo-factor-codigo" />
            )}

            <label className="flex items-center gap-3 text-white/70 text-sm cursor-pointer">
                <input type="checkbox" checked={recordar} onChange={e => setRecordar(e.target.checked)}
                    className="accent-[#FF671F] w-4 h-4" data-testid="segundo-factor-recordar" />
                Recordar este dispositivo 30 días
            </label>

            <Button type="submit" className={BOTON}
                disabled={enviando || (configurando && !qr) || (conRepuesto ? !repuesto.trim() : codigo.length !== 6)}
                data-testid="segundo-factor-enviar">
                {enviando ? <Loader2 className="w-5 h-5 animate-spin" /> : (configurando ? 'Activar y entrar' : 'Entrar')}
            </Button>

            <div className="flex flex-col items-center gap-2">
                {!configurando && (
                    <button type="button" className="text-brand text-sm hover:underline"
                        onClick={() => { setConRepuesto(!conRepuesto); setCodigo(''); setRepuesto(''); }}
                        data-testid="segundo-factor-cambiar">
                        {conRepuesto ? 'Usar el código de la app' : 'No tengo el móvil: usar un código de repuesto'}
                    </button>
                )}
                <button type="button" className="text-white/50 text-sm hover:text-white" onClick={onVolver}>
                    Volver
                </button>
            </div>
        </form>
    );
};

export default SegundoFactorPaso;
